import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { useRouteTracking } from "@/hooks/useRouteTracking";
import { measureFID, measureLCP, preloadCriticalResources } from "@/utils/performance";
import PageFade from "@/components/PageFade";
import { NetworkErrorBoundary } from "@/components/NetworkErrorBoundary";
import React, { lazy, Suspense } from "react";
import WhatsAppButton from "./components/WhatsAppButton";
import { LanguageContext, useLanguageProvider } from "@/hooks/useLanguage";
import { Skeleton } from "@/components/ui/skeleton";

// Eagerly loaded pages (critical path / small)
import Index from "./pages/Index";
import Auth from "./pages/Auth";
import NotFound from "./pages/NotFound";

// ── Lazy-loaded verticals & heavy pages ─────────────────────
const Travel = lazy(() => import("./pages/Travel"));
const Education = lazy(() => import("./pages/Education"));
const Healthcare = lazy(() => import("./pages/Healthcare"));
const Admin = lazy(() => import("./pages/Admin"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const Listings = lazy(() => import("./pages/Listings"));
const MerchantDashboard = lazy(() => import("./pages/MerchantDashboard"));
const MerchantListings = lazy(() => import("./pages/MerchantListings"));
const MerchantLeads = lazy(() => import("./pages/MerchantLeads"));
const Marketplace = lazy(() => import("./pages/Marketplace"));
const PackageBooking = lazy(() => import("./pages/travel/PackageBooking"));
const PackageConfirmed = lazy(() => import("./pages/travel/PackageConfirmed"));
const Flights = lazy(() => import("./pages/travel/Flights"));
const Stays = lazy(() => import("./pages/travel/Stays"));
const TravelSearch = lazy(() => import("./pages/travel/TravelSearch"));
const TravelResults = lazy(() => import("./pages/travel/TravelResults"));
const PassengerDetails = lazy(() => import("./pages/travel/PassengerDetails"));
const Checkout = lazy(() => import("./pages/travel/Checkout"));
const HotelSearch = lazy(() => import("./pages/stays/HotelSearch"));
const HotelResults = lazy(() => import("./pages/stays/HotelResults"));
const HotelCheckout = lazy(() => import("./pages/stays/HotelCheckout"));
const StaysConfirmed = lazy(() => import("./pages/stays/StaysConfirmed"));
const CourseBooking = lazy(() => import("./pages/education/CourseBooking"));
const TreatmentBooking = lazy(() => import("./pages/healthcare/TreatmentBooking"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const Refund = lazy(() => import("./pages/Refund"));
const About = lazy(() => import("./pages/About"));
const Investors = lazy(() => import("./pages/Investors"));
const Contact = lazy(() => import("./pages/Contact"));
const Partners = lazy(() => import("./pages/Partners"));
const BookingConfirmed = lazy(() => import("./pages/BookingConfirmed"));
const FAQ = lazy(() => import("./pages/FAQ"));
const HowWeMakeMoney = lazy(() => import("./pages/HowWeMakeMoney"));
const PayMonthlyTravel = lazy(() => import("./pages/PayMonthlyTravel"));
const PayMonthlyEducation = lazy(() => import("./pages/PayMonthlyEducation"));
const PayMonthlyHealthcare = lazy(() => import("./pages/PayMonthlyHealthcare"));
const PointsDashboard = lazy(() => import("./pages/PointsDashboard"));
const Dashboard = lazy(() => import("./pages/account/Dashboard"));
const Bookings = lazy(() => import("./pages/account/Bookings"));
const Transactions = lazy(() => import("./pages/account/Transactions"));
const Membership = lazy(() => import("./pages/account/Membership"));

import { AccountLayout } from "./components/account/AccountLayout";
import { RequireAuth } from "./components/RequireAuth";

// ── Page skeleton for Suspense fallback ─────────────────────
function PageSkeleton() {
  return (
    <div className="mx-auto max-w-5xl p-6 space-y-6">
      <Skeleton className="h-8 w-1/3" />
      <Skeleton className="h-4 w-2/3" />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <Skeleton className="h-48 rounded-xl" />
        <Skeleton className="h-48 rounded-xl" />
        <Skeleton className="h-48 rounded-xl" />
      </div>
    </div>
  );
}

const queryClient = new QueryClient();

// Route tracking component
function RouteTracker() {
  useRouteTracking();
  return null;
}

const App = () => {
  const languageValue = useLanguageProvider();

  // Initialize performance tracking + capture referral code from URL
  React.useEffect(() => {
    measureFID();
    measureLCP();
    preloadCriticalResources();

    // Capture ?ref= param for referral tracking
    const params = new URLSearchParams(window.location.search);
    const refCode = params.get("ref");
    if (refCode && refCode.startsWith("FXA-")) {
      sessionStorage.setItem("flexaura_referral_code", refCode);
    }
  }, []);

  return (
    <LanguageContext.Provider value={languageValue}>
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <RouteTracker />
            <WhatsAppButton />
            <PageFade>
              <NetworkErrorBoundary>
              <Suspense fallback={<PageSkeleton />}>
                <Routes>
                  <Route path="/" element={<Index />} />
                  <Route path="/admin" element={<Admin />} />
                  <Route path="/admin/dashboard" element={<AdminDashboard />} />
                  <Route path="/auth" element={<Auth />} />
                  <Route path="/listings" element={<Listings />} />
                  <Route path="/merchant" element={<MerchantDashboard />} />
                  <Route path="/merchant/listings" element={<MerchantListings />} />
                  <Route path="/merchant/leads" element={<MerchantLeads />} />
                  <Route path="/marketplace" element={<Marketplace />} />
                  
                  {/* SEO Landing Pages */}
                  <Route path="/pay-monthly-travel" element={<PayMonthlyTravel />} />
                  <Route path="/pay-monthly-education" element={<PayMonthlyEducation />} />
                  <Route path="/pay-monthly-healthcare" element={<PayMonthlyHealthcare />} />
                  
                  <Route path="/travel" element={<Travel />} />
                  <Route path="/travel/search" element={<TravelSearch />} />
                  <Route path="/travel/results" element={<TravelResults />} />
                  <Route path="/travel/passenger-details" element={<PassengerDetails />} />
                  <Route path="/travel/checkout" element={<Checkout />} />
                  <Route path="/travel/confirmed" element={<BookingConfirmed />} />
                  <Route path="/package/confirmed" element={<PackageConfirmed />} />
                  <Route element={<RequireAuth />}>
                    <Route path="/travel/package-booking/:packageId" element={<PackageBooking />} />
                  </Route>
                  <Route path="/travel/flights" element={<Flights />} />
                  <Route path="/travel/stays" element={<Stays />} />
                  <Route path="/stays/search" element={<HotelSearch />} />
                  <Route path="/stays/results" element={<HotelResults />} />
                  <Route path="/stays/checkout" element={<HotelCheckout />} />
                  <Route path="/stays/confirmed" element={<StaysConfirmed />} />
                  <Route path="/education" element={<Education />} />
                  <Route path="/education/course/:courseId" element={<CourseBooking />} />
                  <Route path="/healthcare" element={<Healthcare />} />
                  <Route path="/healthcare/treatment/:treatmentId" element={<TreatmentBooking />} />
                  <Route path="/privacy" element={<Privacy />} />
                  <Route path="/terms" element={<Terms />} />
                  <Route path="/refund" element={<Refund />} />
                  <Route path="/about" element={<About />} />
                  <Route path="/investors" element={<Investors />} />
                  <Route path="/contact" element={<Contact />} />
                  <Route path="/partners" element={<Partners />} />
                  <Route path="/faq" element={<FAQ />} />
                  <Route path="/transparency" element={<HowWeMakeMoney />} />
                  <Route path="/booking/confirmed" element={<BookingConfirmed />} />
                  <Route path="/points" element={<PointsDashboard />} />
                  <Route path="/account" element={<AccountLayout />}>
                    <Route path="dashboard" element={<Dashboard />} />
                    <Route path="bookings" element={<Bookings />} />
                    <Route path="transactions" element={<Transactions />} />
                    <Route path="membership" element={<Membership />} />
                    <Route path="points" element={<PointsDashboard />} />
                  </Route>
                  {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
              </NetworkErrorBoundary>
            </PageFade>
          </BrowserRouter>
        </TooltipProvider>
      </QueryClientProvider>
    </HelmetProvider>
    </LanguageContext.Provider>
  );
};

export default App;
